import React, { PropsWithChildren, useState } from 'react'
import { useTheme } from '@material-ui/core/styles'
import styled, { css } from 'styled-components'
import { SecondaryBanner } from '../components/secondarybanner/SecondaryBanner'
import { SecondaryBannerHero } from '../components/secondarybanner/SecondaryBannerHero'
import {
  StyledBreadcrumbs,
  StyledLinkSection,
  StyledSubtitle,
  StyledTitle,
} from '../components/secondarybanner/StyledSecondaryBanner'
import { rem } from '../utils'
import { isNotNull, nonNull } from '../utils/nonNull'
import { ProductFeaturesSubBannerFragment, SecondaryPageBannerFragment } from '@gql'
import { StyledFlexibleContentWrapper } from '../components/flexibleContent'
import { AltBg } from '../components/flexibleContent/fragments/altBg'
import {
  StyledBSBLabel,
  StyledQTLabel, StyledQuickLinksFullWidthWrapper,
  StyledQuickLinksToggle, StyledQuickLinksWrapper,
  StyledToggleIcon,
} from '../templates/productListing/styledProductListing'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { Container, Grid } from '@material-ui/core'
import { QuickLinks } from '../templates/productListing/fragments'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { SlantedYellowBackground, StyledContainer } from '../templates/productDetails/styledProductDetails'
import { FCPRatesFeatureListing } from '../components/flexibleContent/fragments/fcpRatesFeatureListing'

export type SecondaryPageBannerProps = SecondaryPageBannerFragment & {
  breadcrumbs?: React.ReactNode
  after?: 'grey' | 'white' | 'light'
  // Only FCP product pages opt into the full-width "hero" style (bannerStyle).
  allowHeroStyle?: boolean
}

const StyledSecondaryBanner = styled(SecondaryBanner)<{ breadcrumbs: React.ReactNode }>`
  > .MuiContainer-root {
    height: 100%;

    > .MuiGrid-container {
      height: 100%;

      > .MuiGrid-item {
        display: flex;
        flex-direction: column;
      }
    }
  }

  ${props => props.theme.breakpoints.up('md')} {
    padding-bottom: 0;

    .MuiBreadcrumbs-ol {
      margin-top: ${props => rem(20, props.theme)};
      max-width: ${props => rem(350, props.theme)};
      flex-wrap: nowrap;

      ${props => props.theme.breakpoints.up('md')} {
        max-width: 100%;
      }

      .MuiBreadcrumbs-li {
        flex: 0 0 auto;
        white-space: nowrap;

        &:last-child {
          flex: 0 1 auto;
          overflow: hidden;

          > a {
            max-width: 100%;

            p {
              max-width: 100%;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }
        }

        &:focus-within {
          outline: ${props => props.theme.typography.pxToRem(1)} auto -webkit-focus-ring-color;
        }
      }
    }
  }

  ${props => !props.src && css`
    ${StyledTitle} {
      margin-top: ${props => rem(75, props.theme)};
      max-width: 100%;
      text-align: left;
      padding: 0;

      ${props => props.theme.breakpoints.up('md')} {
        margin-top: 0;
      }
    }

    ${StyledSubtitle} {
      max-width: ${props => rem(600, props.theme)};
      text-align: left;
    }

    ${StyledBreadcrumbs} {
      background: none;
      margin: ${props => rem(10, props.theme)} 0 0 ${props => rem(3, props.theme)};

      ${props => props.theme.breakpoints.up('md')} {
        margin: 0;
      }
    }

    ${StyledLinkSection} {
      justify-content: flex-start;
    }
  `}
`

// Spacing between the banner and the section that follows it. Applied to both
// the standard banner wrapper and the full-width hero so the gap is identical
// regardless of layout. When the next section leads with an AltBg (coloured)
// block, the margin collapses and the AltBg's own padding provides the gap.
const bannerSiblingSpacing = css`
  + ${StyledQuickLinksFullWidthWrapper} + ${StyledFlexibleContentWrapper},
  + ${StyledFlexibleContentWrapper} {
      margin-top: ${props => rem(50, props.theme)};

      ${props => props.theme.breakpoints.up('md')} {
          margin-top: ${props => rem(100, props.theme)};
      }

      &:has(${AltBg}:first-child) {
          margin-top: 0;
      }

      ${AltBg}:first-child {
          padding-top: ${props => rem(50, props.theme)};

          ${props => props.theme.breakpoints.up('md')} {
              padding-top: ${props => rem(100, props.theme)};
          }
      }
  }
`

export const StyledWrapper = styled.div`
  position: relative;
  background: linear-gradient(43deg, #E37025 15%, #EBA719 50%);

  ${props => props.theme.breakpoints.up('md')} {
    background: linear-gradient(90deg, #D25015 0%, #DD671B 20%, #E58826 40%);
  }

  ${props => props.theme.breakpoints.up('xl')} {
      background: linear-gradient(90deg, #D25015 0%, #DD671B 23%, #E58826 50%);
  }

  ${bannerSiblingSpacing}
`

const StyledSecondaryBannerHero = styled(SecondaryBannerHero)`
  ${bannerSiblingSpacing}
`

type SubBanner = NonNullable<SecondaryPageBannerFragment['subBanner']>[number]

type QuickLinksBlock = Extract<SubBanner, {
  __typename: 'subBanner_quickLinks_BlockType'
}>

function isQuickLinksBlock(block: SubBanner): block is QuickLinksBlock {
  return block?.__typename === 'subBanner_quickLinks_BlockType'
}

function isProductFeatureBlock(block: SubBanner): block is ProductFeaturesSubBannerFragment {
  return block?.__typename === 'subBanner_productFeatures_BlockType'
}

export const SecondaryPageBanner: React.FC<PropsWithChildren<SecondaryPageBannerProps>> = ({ breadcrumbs, title, subTitle, image, primaryButton, secondaryButton, subBanner, bannerStyle, textAlignment, secondaryButtonType, videoLink, allowHeroStyle }) => {
  const imgSrc = image ? image.find(x => x?.__typename === 'images_Asset')?.url : null
  // @ts-ignore
  const quickLinks = subBanner?.find(isQuickLinksBlock)?.fcpSubBannerQuicklinks ?? []
  const [showQuickLinks, setShowQuickLinks] = useState(false)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const toggleQuickLinks = () => {
    if (!isDesktop) {
      setShowQuickLinks((prev) => !prev)
    }
  }

  const productFeatures = subBanner?.find(isProductFeatureBlock)

  // Full-width "hero" style — gated to FCP product pages via allowHeroStyle.
  const useHeroStyle = Boolean(allowHeroStyle && bannerStyle)

  return (
    <>
      {useHeroStyle ? (
        <StyledSecondaryBannerHero
          breadcrumbs={breadcrumbs}
          title={title}
          subTitle={subTitle}
          src={imgSrc}
          videoLink={videoLink}
          align={textAlignment ? 'right' : 'left'}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          lightSecondaryButton={Boolean(secondaryButtonType)}
        />
      ) : (
        <StyledWrapper>
          <StyledSecondaryBanner image breadcrumbs={breadcrumbs} src={imgSrc ?? undefined}>
            {title && (
              <SecondaryBanner title>{title}</SecondaryBanner>
            )}
            {subTitle && (
              <SecondaryBanner subtitle>{subTitle}</SecondaryBanner>
            )}
            {(primaryButton || secondaryButton) && (
              <SecondaryBanner section>
                {(primaryButton && primaryButton.__typename === 'linkField_Link' && isNotNull(primaryButton.url)) && (
                  <SecondaryBanner cta buttonProps={{
                    primary: true,
                    buttonColor: 'orange',
                    target: primaryButton?.target === '_blank' ? '_blank' : '_self',
                  }} href={primaryButton.url || '#'}>
                    {primaryButton.customText}
                  </SecondaryBanner>
                )}
                {(secondaryButton && secondaryButton.__typename === 'linkField_Link' && isNotNull(secondaryButton.url)) &&
                  <SecondaryBanner cta buttonProps={{
                    primary: true,
                    buttonColor: 'black',
                    target: secondaryButton?.target === '_blank' ? '_blank' : '_self',
                    size: 'small',
                  }} href={secondaryButton.url || '#'}>
                    {secondaryButton.customText}
                  </SecondaryBanner>
                }
              </SecondaryBanner>
            )}
          </StyledSecondaryBanner>
        </StyledWrapper>
      )}
      {quickLinks.length > 0 && (
        <StyledQuickLinksFullWidthWrapper>
          <>
            <StyledQuickLinksToggle onClick={toggleQuickLinks} open={showQuickLinks}>
              <StyledQTLabel>Jump to <StyledToggleIcon icon={showQuickLinks ? faChevronUp : faChevronDown} /></StyledQTLabel>
              <StyledBSBLabel><span>BSB</span> 805 007</StyledBSBLabel>
            </StyledQuickLinksToggle>

            <StyledQuickLinksWrapper
              container
              item
              lg={12}
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              wrap="nowrap"
              $isVisible={showQuickLinks}
            >
              <Container>
                <QuickLinks data={nonNull(quickLinks)} />
              </Container>
            </StyledQuickLinksWrapper>
          </>
        </StyledQuickLinksFullWidthWrapper>
      )}
      {productFeatures && (
        <StyledQuickLinksFullWidthWrapper>
          <SlantedYellowBackground>
            <Container maxWidth={'lg'}>
              <StyledContainer direction={'column'} alignItems={'center'} container>
                <Grid xs={12} item component={'section'}>
                  <FCPRatesFeatureListing data={productFeatures} />
                </Grid>
              </StyledContainer>
            </Container>
          </SlantedYellowBackground>
        </StyledQuickLinksFullWidthWrapper>
      )}
    </>
  )}
